import React from 'react';
import { Star, MapPin, Phone, Globe, Shield, Award } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { trackLead, trackConversion } from '../utils/analytics';
import { ImageWithFallback } from './ImageWithFallback';
import type { Plumber } from '../types';

interface PlumberCardProps {
  plumber: Plumber;
}

export function PlumberCard({ plumber }: PlumberCardProps) {
  const [serviceNames, setServiceNames] = React.useState<Record<string, string>>({});
  const [cityNames, setCityNames] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadServices() {
      setLoading(true);
      const { data: services } = await supabase
        .from('plumber_services')
        .select(`
          service:service_id(
            id,
            title
          )
        `)
        .eq('plumber_id', plumber.id);
      
      if (services) {
        const serviceMap = services.reduce((acc, service) => {
          if (service.service) {
            acc[service.service.id] = service.service.title;
          }
          return acc;
        }, {} as Record<string, string>);
        setServiceNames(serviceMap);
      }
    }
    
    async function loadCities() {
      const { data: cities } = await supabase
        .from('cities')
        .select(`
          id,
          name,
          state:state_id(code)
        `)
        .in('id', plumber.service_areas.map(area => area.city_id).filter(id => id));
      
      if (cities) {
        const names = cities.map(city => `${city.name}, ${city.state.code}`);
        setCityNames(names);
      } else {
        setCityNames([]);
      }
      setLoading(false);
    }

    loadServices();
    if (plumber.service_areas?.length > 0) loadCities();
    else {
      setLoading(false);
    }
  }, [plumber.services]);

  const handlePhoneClick = async () => {
    // Track phone click as lead
    await trackLead('phone_click', {
      plumber_id: plumber.id,
      business_name: plumber.business_name,
      city_id: plumber.service_areas[0]?.city_id,
      service_areas: plumber.service_areas.map(area => ({
        zip_code: area.zip_code,
        city: area.city?.name,
        state: area.city?.state?.code
      }))
    });
    
    // Track phone click as conversion
    await trackConversion('phone_click', {
      plumber_id: plumber.id,
      business_name: plumber.business_name,
      city_id: plumber.service_areas[0]?.city_id,
      contact_method: 'phone',
      value: 1
    });
  };

  const handleWebsiteClick = async () => {
    // Track website visit as lead
    await trackLead('website_visit', {
      plumber_id: plumber.id,
      business_name: plumber.business_name,
      city_id: plumber.service_areas[0]?.city_id,
      website: plumber.website,
      service_areas: plumber.service_areas.map(area => ({
        zip_code: area.zip_code,
        city: area.city?.name,
        state: area.city?.state?.code
      }))
    });
    
    // Track website visit as conversion
    await trackConversion('website_visit', {
      plumber_id: plumber.id,
      business_name: plumber.business_name,
      city_id: plumber.service_areas[0]?.city_id,
      contact_method: 'website',
      value: 1
    });
  };

  // Create a Set of unique locations to avoid duplicates
  const uniqueLocations = new Set(
    plumber.service_areas.map(area => `${area.city}, ${area.state}`)
  );
  const serviceAreas = Array.from(uniqueLocations).join(', ');

  return (
    <article 
      className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow ${
        plumber.is_featured ? 'ring-2 ring-yellow-400 bg-gradient-to-br from-yellow-50 to-white' : ''
      }`}
      itemScope itemType="https://schema.org/LocalBusiness"
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900" itemProp="name">
              {plumber.business_name}
              {plumber.is_featured && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                  <Award className="w-3 h-3 mr-1" />
                  Featured
                </span>
              )}
              {plumber.is_verified && (
                <Shield className="inline-block w-5 h-5 ml-2 text-blue-600" />
              )}
            </h3>
            <div className="flex items-center mt-1">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < Math.round(plumber.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="ml-2 text-sm text-gray-600">
                ({plumber.reviews.length} reviews)
              </span>
            </div>
          </div>
          <div className="text-right">
            <a
              href={`tel:${plumber.phone}`}
              itemProp="telephone"
              onClick={handlePhoneClick}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </a>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {plumber.description && (
            <p className="text-gray-600 line-clamp-2" itemProp="description">{plumber.description}</p>
          )}
          <div className="flex items-start gap-2">
            <MapPin className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
            <p className="ml-2 text-sm text-gray-600" itemProp="areaServed">
              {loading ? (
                <span className="animate-pulse">Loading service areas...</span>
              ) : cityNames.length > 0 ? (
                cityNames.join(', ')
              ) : (
                'Serving all areas'
              )}
            </p>
          </div>
          {plumber.website && (
            <div className="flex items-center">
              <Globe className="w-5 h-5 text-gray-400" />
              <a
                href={plumber.website}
                itemProp="url"
                onClick={handleWebsiteClick}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-sm text-blue-600 hover:underline"
              >
                Visit Website
              </a>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {plumber.services.map(service => (
            <span
              key={service.service_id}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {serviceNames[service.service_id]?.replace('24/7 ', '') || 'Loading...'}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}